import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Close, Error, LegendToggle} from "@mui/icons-material";
import {Box, Button, Card, CardHeader, CircularProgress, IconButton, useTheme} from "@mui/material";
import {useTranslation, withTranslation} from "react-i18next";
import {connect} from "react-redux";
import {compose} from "redux";
import {v4 as uuidv4} from "uuid";
import CustomEmpty from "../../components/custom-empty";
import {
  showMessage,
  SNACKBAR_MESSAGE_SEVERITY_INFO,
  SNACKBAR_MESSAGE_SEVERITY_WARNING
} from "../../hocs/snackbar-provider";
import IdentifyResults from "./IdentifyResults";
import WmsLayersLegend from "./WmsLayersLegend";
import {getLayerOptions, getTranslationOptions, MAP_LAYERS_ALL} from "../../utils/map";
import "./style.css";

const LMap = window.LMap;

const mapStateToProps = state => {
  return {
    defaultLanguage: state.app.language,
    languages: state.app.languages,
    mapLayersConfig: state.app.mapLayersConfig,
    mapLayer: state.hub?.hub?.extras?.MapLayer || null,
    defaultBaseLayer: state.appConfig?.wmsMapConfig?.baseLayer,
    defaultExtent: state.appConfig?.wmsMapConfig?.defaultExtent,
    infoTolerance: state.appConfig?.wmsMapConfig?.infoTolerance,
    opacity: state.appConfig?.wmsMapConfig?.opacity,
    projDefsUrl: state.appConfig?.wmsMapConfig?.projDefsUrl,
    proxyUrl: state.appConfig?.wmsMapConfig?.proxyUrl,
    labelFieldList: state.appConfig?.wmsMapConfig?.labelFieldList,
    wmsServiceUrlFieldList: state.appConfig?.wmsMapConfig?.wmsServiceUrlFieldList
  };
};

const OVERLAY_TYPE_LOADING = "loading";
const OVERLAY_TYPE_ERROR = "error";

const WmsMap = ({
  defaultLanguage,
  languages,
  mapLayersConfig,
  mapLayer,
  defaultBaseLayer,
  defaultExtent,
  infoTolerance,
  opacity,
  proxyUrl,
  labelFieldList,
  wmsServiceUrlFieldList,

  serviceUrl,
  visibleLayers: externalVisibleLayers,
  disableBaseLayer = false,
  disableWheelZoom = false
}) => {
  const {t} = useTranslation();
  const theme = useTheme();

  const loadingTimer = useRef(null);

  const mapId = useMemo(() => `uuid__${uuidv4()}`, []);

  const [mapInitialized, setMapInitialized] = useState(false);

  const [overlayType, setOverlayType] = useState(null);

  // layers legend
  const [isLayersLegendVisibile, setLayersLegendVisibility] = useState(false);
  const [layers, setLayers] = useState([]);
  const [visibleLayers, setVisibleLayers] = useState([]);

  const hideLayersLegendDialog = useCallback(() => {
    setLayersLegendVisibility(false);
  }, []);

  const showLayersLegendDialog = useCallback(() => {
    setLayersLegendVisibility(true);
  }, []);

  // identify results
  const [isIdentifyResultsVisible, setIdentifyResultsVisibility] = useState(false);
  const [identifyResults, setIdentifyResults] = useState(null);

  const showIdentifyResultsDialog = useCallback(identifyResult => {
    setIdentifyResultsVisibility(true);
    setIdentifyResults(identifyResult);
  }, []);

  const hideIdentifyResultsDialog = useCallback(() => {
    setIdentifyResultsVisibility(false);
    setIdentifyResults(null);
  }, []);

  const identifiedFeaturesCount = useMemo(() => {
    let featuresCount = 0;
    Object.keys(identifyResults || {}).forEach(key => {
      const features = identifyResults[key];
      featuresCount += (features || []).length;
    });
    return featuresCount;
  }, [identifyResults]);

  const handleVisibleLayers = (layerName, checked) => {
    setVisibleLayers(prev => {
      let next = checked ? [...prev, layerName] : prev.filter(l => l !== layerName);

      // Re-order according to current layers array
      next = layers.filter(l => next.includes(l.LayerName)).map(l => l.LayerName);

      if (next.length === 0) {
        showMessage(t("modules.viewerWmsMap.layers.emptySelection.label"), SNACKBAR_MESSAGE_SEVERITY_WARNING);
        return prev; // keep previous (avoid empty)
      }

      LMap.setWmsVisibleLayers(mapId, next);
      return next;
    });
  };

  useEffect(() => {
    const {layers, baseLayer} = getLayerOptions(
      mapLayer,
      defaultBaseLayer,
      mapLayersConfig,
      defaultLanguage,
      languages
    );

    const mapOptions = {
      showZoom: true,
      showScale: false,
      baseLayer: baseLayer,
      layers: layers,
      defaultExtent: defaultExtent,
      showBaseLayerRadio: !disableBaseLayer && mapLayer === MAP_LAYERS_ALL && (layers || []).length > 1,
      showSelection: false,
      disableWheelZoom: disableWheelZoom,
      isBaseLayerRadioPopupClosable: true,
      isSimpleInteraction: true,
      infoTolerance: infoTolerance,
      legendFontFamily: "Roboto",
      primaryColor: theme.palette.text.primary,
      onIdentifyStart: () => {
        setOverlayType(OVERLAY_TYPE_LOADING);
      },
      onIdentify: identifyResult => {
        if (Object.keys(identifyResult || {}).length > 0) {
          showIdentifyResultsDialog(identifyResult);
        } else {
          showMessage(t("modules.viewerWmsMap.identifyDialog.noResults.label"), SNACKBAR_MESSAGE_SEVERITY_INFO);
          hideIdentifyResultsDialog();
        }
        setOverlayType(null);
      }
    };

    const mapTranslations = getTranslationOptions(t);

    LMap.initMap(mapId, mapOptions, mapTranslations, () => {
      setMapInitialized(true);
      setOverlayType(OVERLAY_TYPE_LOADING);
    });

    return () => {
      LMap.destroyMap(mapId);
      setMapInitialized(false);
      setOverlayType(null);
    };
  }, [
    mapId,
    defaultLanguage,
    languages,
    mapLayersConfig,
    mapLayer,
    defaultBaseLayer,
    defaultExtent,
    infoTolerance,
    disableBaseLayer,
    disableWheelZoom,
    showIdentifyResultsDialog,
    hideIdentifyResultsDialog,
    t,
    theme.palette.text.primary
  ]);

  useEffect(() => {
    if (mapInitialized) {
      if (!serviceUrl) {
        showMessage(t("modules.viewerWmsMap.messages.errorContactingService"), SNACKBAR_MESSAGE_SEVERITY_WARNING);
        setOverlayType(OVERLAY_TYPE_ERROR);
        return;
      }

      LMap.removeLayer(mapId);
      hideIdentifyResultsDialog();

      const wmsLayerOptions = {
        serviceURL: serviceUrl,
        visibleLayers: [],
        proxyUrl: proxyUrl,
        disableZoomToLayer: false,
        settings: {
          opacity: opacity
        }
      };

      LMap.addWmsLayer(
        mapId,
        null,
        wmsLayerOptions,
        withErrors => {
          if (withErrors) {
            if (loadingTimer.current) clearTimeout(loadingTimer.current);
            showMessage(t("modules.viewerWmsMap.messages.errorContactingService"), SNACKBAR_MESSAGE_SEVERITY_WARNING);
            setOverlayType(OVERLAY_TYPE_ERROR);
          } else {
            let layers = LMap.getWmsLegend(mapId) || [];
            if ((externalVisibleLayers || []).length > 0) {
              layers = layers.filter(layer => externalVisibleLayers.includes(layer.LayerName));
            }
            if (layers.length == 0) {
              LMap.removeLayer(mapId);
              setOverlayType(OVERLAY_TYPE_ERROR);
              showMessage(
                t("modules.viewerWmsMap.noLayersAvailable", {count: (externalVisibleLayers || []).length}),
                SNACKBAR_MESSAGE_SEVERITY_WARNING
              );
            } else {
              setLayers(layers);
              setVisibleLayers(layers.map(layerLegend => layerLegend.LayerName));
              LMap.setWmsVisibleLayers(
                mapId,
                layers.map(layerLegend => layerLegend.LayerName)
              );
              showLayersLegendDialog();
            }
          }
        },
        () => {
          if (loadingTimer.current) clearTimeout(loadingTimer.current);
          loadingTimer.current = setTimeout(() => {
            setOverlayType(OVERLAY_TYPE_LOADING);
          }, 300);
        },
        () => {
          if (loadingTimer.current) clearTimeout(loadingTimer.current);
          setOverlayType(null);
        },
        () => {
          if (loadingTimer.current) clearTimeout(loadingTimer.current);
          setOverlayType(null);
          showMessage(t("modules.viewerWmsMap.messages.errorFetchingImage"), SNACKBAR_MESSAGE_SEVERITY_WARNING);
        }
      );
    }
  }, [
    mapId,
    mapInitialized,
    serviceUrl,
    opacity,
    proxyUrl,
    hideIdentifyResultsDialog,
    showLayersLegendDialog,
    externalVisibleLayers,
    t
  ]);

  return (
    <Box
      className="viewer-wms-map__wrapper"
      sx={{
        width: "100%",
        height: "100%"
      }}
    >
      <Box
        className="viewer-wms-map"
        sx={{
          width: "100%",
          height: "100%",
          position: "relative"
        }}
      >
        <Box
          id={mapId}
          className="viewer-wms-map__lmap-container"
          sx={{
            width: "100%",
            height: "100%",
            filter: overlayType ? "blur(1px)" : ""
          }}
        />

        {layers.length > 0 && (
          <>
            <Button
              onClick={showLayersLegendDialog}
              startIcon={<LegendToggle />}
              className="viewer-wms-map__legend-btn"
              sx={{
                position: "absolute",
                zIndex: 1,
                top: 16,
                right: 16,
                filter: overlayType ? "blur(1px)" : "",
                background: "white",
                opacity: "0.8",
                boxShadow: "0 2px 1px -1px #0003, 0 1px 1px 0 #00000024, 0 1px 3px 0 #0000001f",
                border: "1px solid #d9d9d9",
                "&:hover": {
                  borderColor: "var(--text-primary)"
                }
              }}
            >
              {t("modules.viewerWmsMap.layers.title")}
            </Button>

            <Box
              className="viewer-wms-map__legend-container"
              sx={{
                display: isLayersLegendVisibile ? "block" : "none",
                width: "400px",
                height: "100%",
                position: "absolute",
                zIndex: 1,
                top: 0,
                right: 0
              }}
            >
              <Card
                className="viewer-wms-map__legend-container__card"
                sx={{
                  width: "100%",
                  height: "100%",
                  padding: "8px",
                  backgroundColor: "#f5f5f5"
                }}
              >
                <CardHeader
                  title={t("modules.viewerWmsMap.layers.title")}
                  className="viewer-wms-map__legend-container__card__card-header"
                  slotProps={{
                    title: {
                      variant: "h6",
                      sx: {
                        fontSize: "16px"
                      }
                    }
                  }}
                  sx={{
                    padding: "12px"
                  }}
                  action={
                    <IconButton onClick={hideLayersLegendDialog}>
                      <Close />
                    </IconButton>
                  }
                />
                <Box
                  className="viewer-wms-map__legend-container__card__content"
                  sx={{
                    height: "calc(100% - 56px)",
                    overflow: "auto"
                  }}
                >
                  <WmsLayersLegend layers={layers} visibleLayers={visibleLayers} onChange={handleVisibleLayers} />
                </Box>
              </Card>
            </Box>
          </>
        )}

        <Box
          className="viewer-wms-map__identify-results-container"
          sx={{
            display: isIdentifyResultsVisible ? "block" : "none",
            width: "400px",
            height: "100%",
            position: "absolute",
            zIndex: 1,
            top: 0,
            right: 0
          }}
        >
          {Object.keys(identifyResults || {}).length > 0 && (
            <Card
              className="viewer-wms-map__identify-results-container__card"
              sx={{
                width: "100%",
                height: "100%",
                padding: "8px",
                backgroundColor: "#f5f5f5"
              }}
            >
              <CardHeader
                className="viewer-wms-map__identify-results-container__card__card-header"
                title={t("modules.viewerWmsMap.identifyDialog.title", {
                  count: identifiedFeaturesCount
                })}
                action={
                  <IconButton onClick={hideIdentifyResultsDialog}>
                    <Close />
                  </IconButton>
                }
                slotProps={{
                  title: {
                    variant: "h6",
                    sx: {
                      fontSize: "16px"
                    }
                  }
                }}
                sx={{
                  padding: "12px"
                }}
              />
              <Box
                className="viewer-wms-map__identify-results-container__card__content"
                sx={{
                  height: "calc(100% - 56px)",
                  overflow: "auto"
                }}
              >
                <IdentifyResults
                  visibleLayers={visibleLayers}
                  identifyResults={identifyResults}
                  labelFieldList={labelFieldList}
                  wmsServiceUrlFieldList={wmsServiceUrlFieldList}
                  language={defaultLanguage}
                />
              </Box>
            </Card>
          )}
        </Box>

        {overlayType && (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              position: "absolute",
              zIndex: 1,
              top: 0,
              left: 0
            }}
          >
            <CustomEmpty
              text={
                overlayType === OVERLAY_TYPE_LOADING
                  ? t("modules.viewerWmsMap.overlayMessages.loading") + "..."
                  : overlayType === OVERLAY_TYPE_ERROR
                    ? t("modules.viewerWmsMap.overlayMessages.error")
                    : null
              }
              textStyle={{
                color: "#ffffff",
                fontWeight: "bold"
              }}
              image={
                overlayType === OVERLAY_TYPE_LOADING ? (
                  <CircularProgress style={{color: "#ffffff"}} />
                ) : overlayType === OVERLAY_TYPE_ERROR ? (
                  <Error style={{color: "#ffffff"}} />
                ) : null
              }
              sx={{
                backgroundColor: "rgba(0, 0, 0, 0.1)"
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default compose(withTranslation(), connect(mapStateToProps))(WmsMap);
